import { useMemo, useState } from 'react';
import { categories, hotEvents } from '@/mocks/gossip';
import SectionHeading from './SectionHeading';
import HotRank from './HotRank';

const PAGE_SIZE = 8;

const catMeta: Record<string, { label: string; chip: string }> = {
  ent: { label: '娱乐圈', chip: 'bg-primary-100 text-primary-700' },
  film: { label: '影视综艺', chip: 'bg-accent-100 text-accent-700' },
  social: { label: '社会热点', chip: 'bg-secondary-100 text-secondary-700' },
  tech: { label: '科技数码', chip: 'bg-secondary-100 text-secondary-700' },
  sports: { label: '体坛动态', chip: 'bg-accent-100 text-accent-700' },
  finance: { label: '财经楼市', chip: 'bg-primary-100 text-primary-700' },
  game: { label: '游戏电竞', chip: 'bg-primary-100 text-primary-700' },
};

const markStyle: Record<string, string> = {
  爆: 'bg-primary-500 text-background-50',
  热: 'bg-accent-500 text-background-50',
  新: 'bg-secondary-500 text-background-50',
};

interface FeedProps {
  query: string;
}

export default function HotFeed({ query }: FeedProps) {
  const [catKey, setCatKey] = useState('all');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return hotEvents.filter((e) => {
      const inCat = catKey === 'all' || e.catKey === catKey;
      const inSearch = !q || e.title.toLowerCase().includes(q) || e.summary.toLowerCase().includes(q);
      return inCat && inSearch;
    });
  }, [catKey, query]);

  const shown = filtered.slice(0, visible);

  return (
    <section id="feed" className="scroll-mt-20 bg-background-100/60 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="热门瓜料"
          title="最新瓜料 · 热点打包"
          desc="涵盖娱乐圈、影视综艺、社会热点、科技数码、体坛动态、财经楼市、游戏电竞等栏目，按热度聚合当天值得一看的事件。"
          accentIcon="ri-fire-fill"
        />

        {/* 分类切换 */}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {categories.map((c) => (
            <button
              key={c.key}
              onClick={() => {
                setCatKey(c.key);
                setVisible(PAGE_SIZE);
              }}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-1 py-1 transition-colors ${
                catKey === c.key ? 'text-primary-700' : 'text-foreground-600 hover:text-primary-600'
              }`}
            >
              <span
                className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                  catKey === c.key
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 hover:bg-background-200'
                }`}
              >
                <i className={`${c.icon} text-base`} />
                {c.label}
              </span>
            </button>
          ))}
        </div>

        {query && (
          <p className="mt-4 text-sm text-foreground-600">
            搜索“<span className="font-semibold text-primary-700">{query}</span>”结果共{' '}
            <span className="font-semibold text-primary-700">{filtered.length}</span> 条：
          </p>
        )}

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_340px]">
          {/* 信息流列表 */}
          <div>
            {shown.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
                <i className="text-4xl text-foreground-300 ri-folder-forbid-line" />
                <p className="mt-3 text-sm font-medium text-foreground-600">没有找到相关瓜料</p>
                <p className="mt-1 text-xs text-foreground-500">换个关键词或栏目试试吧</p>
                <button
                  onClick={() => {
                    setCatKey('all');
                    setVisible(PAGE_SIZE);
                  }}
                  className="mt-4 whitespace-nowrap rounded-md bg-accent-100 px-4 py-2 text-sm font-semibold text-accent-700 transition-colors hover:bg-accent-200"
                >
                  查看全部
                </button>
              </div>
            ) : (
              <ul className="space-y-3">
                {shown.map((e) => {
                  const meta = catMeta[e.catKey];
                  return (
                    <li
                      key={e.id}
                      className="group rounded-2xl border border-background-200 bg-background-50 p-4 transition-all hover:border-primary-200 hover:shadow-lift md:p-5"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${meta.chip}`}>
                          {meta.label}
                        </span>
                        {e.tags.slice(0, 2).map((t) => (
                          <span key={t} className="rounded-full bg-background-100 px-2 py-0.5 text-xs text-foreground-500">
                            #{t}
                          </span>
                        ))}
                        {e.mark && (
                          <span className={`flex h-5 w-5 items-center justify-center rounded-md text-[10px] font-bold ${markStyle[e.mark]}`}>
                            {e.mark}
                          </span>
                        )}
                        <div className="ml-auto flex items-center gap-1 text-xs text-foreground-500">
                          <i className="ri-time-line text-sm" />
                          <span className="whitespace-nowrap">{e.time}</span>
                        </div>
                      </div>

                      <a
                        href="#feed"
                        onClick={(ev) => ev.preventDefault()}
                        className="mt-2.5 block text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg"
                      >
                        {e.title}
                      </a>
                      <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                        {e.summary}
                      </p>

                      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                        <span className="inline-flex items-center gap-1">
                          <i className="text-sm ri-fire-line" />
                          热度 {e.heat} 万
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <i className="text-sm ri-chat-3-line" />
                          {e.comments.toLocaleString()} 条讨论
                        </span>
                        <a
                          href="#feed"
                          onClick={(ev) => ev.preventDefault()}
                          className="ml-auto inline-flex items-center gap-1 whitespace-nowrap font-semibold text-primary-600 transition-colors hover:text-primary-700"
                        >
                          查看详情
                          <i className="ri-arrow-right-line" />
                        </a>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {shown.length < filtered.length && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
                >
                  加载更多瓜料
                  <i className="ri-arrow-down-line" />
                </button>
              </div>
            )}
          </div>

          {/* 右侧栏 */}
          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <HotRank />
            <div className="rounded-2xl border border-background-200 bg-background-100/70 p-5">
              <div className="flex items-center gap-2">
                <i className="text-lg text-accent-600 ri-customer-service-2-line" />
                <h3 className="font-heading text-lg font-bold text-foreground-950">今日已更新</h3>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                每日早、午、晚定时汇总，将热点事件打包整理，重要事件持续跟进至尘埃落定。
              </p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-500">早间速览</span>
                  <span className="font-semibold text-foreground-800">08:00 · 已推送</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-background-200">
                  <div className="h-full w-full rounded-full bg-accent-500" />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-500">午间追踪</span>
                  <span className="font-semibold text-foreground-800">12:30 · 已推送</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-background-200">
                  <div className="h-full w-full rounded-full bg-primary-500" />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-500">晚间打包</span>
                  <span className="font-semibold text-foreground-800">20:00 · 待推送</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-background-200">
                  <div className="h-full w-1/3 rounded-full bg-primary-300" />
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}